export type InvoiceAnalyticsPreset =
  | 'today'
  | 'last7'
  | 'this_month'
  | 'last_6_months'
  | 'last_month'
  | 'this_year'
  | 'custom';

export const INVOICE_ANALYTICS_PRESETS: { value: InvoiceAnalyticsPreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'last7', label: 'Last 7 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_6_months', label: 'Last 6 months' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_year', label: 'This year' },
  { value: 'custom', label: 'Custom' },
];

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Format a UTC calendar day as YYYY-MM-DD. */
export function toUtcYmd(year: number, monthIndex0: number, day: number): string {
  return `${year}-${pad2(monthIndex0 + 1)}-${pad2(day)}`;
}

export function utcTodayYmd(now = new Date()): string {
  return toUtcYmd(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

export function addUtcDays(ymd: string, deltaDays: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + deltaDays);
  return toUtcYmd(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
}

export function rangeForPreset(
  preset: Exclude<InvoiceAnalyticsPreset, 'custom'>,
  now = new Date()
): { date_from: string; date_to: string } {
  const today = utcTodayYmd(now);
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();

  switch (preset) {
    case 'today':
      return { date_from: today, date_to: today };
    case 'last7':
      return { date_from: addUtcDays(today, -6), date_to: today };
    case 'this_month':
      return { date_from: toUtcYmd(y, m, 1), date_to: today };
    case 'last_6_months': {
      const from = new Date(Date.UTC(y, m - 6, now.getUTCDate()));
      return {
        date_from: toUtcYmd(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate()),
        date_to: today,
      };
    }
    case 'last_month': {
      const lastMonthEnd = new Date(Date.UTC(y, m, 0));
      const from = toUtcYmd(lastMonthEnd.getUTCFullYear(), lastMonthEnd.getUTCMonth(), 1);
      const to = toUtcYmd(
        lastMonthEnd.getUTCFullYear(),
        lastMonthEnd.getUTCMonth(),
        lastMonthEnd.getUTCDate()
      );
      return { date_from: from, date_to: to };
    }
    case 'this_year':
      return { date_from: toUtcYmd(y, 0, 1), date_to: today };
    default:
      return { date_from: toUtcYmd(y, m, 1), date_to: today };
  }
}

export function thisMonthRange(now = new Date()): { date_from: string; date_to: string } {
  return rangeForPreset('this_month', now);
}

export function last6MonthsRange(now = new Date()): { date_from: string; date_to: string } {
  return rangeForPreset('last_6_months', now);
}
